import { useSelector } from 'react-redux';
import React, { useMemo } from 'react';
import { useParams } from 'react-router';

import { useGetMultiplePartnerDetails } from 'hooks/useGetMultiplePartnerDetails';
import { useGetPartnerDetails } from 'hooks/useGetPartnerDetails';
import { GlobalState } from 'reducers/stateTypes';
import ContactNote, { ContactNoteDialog } from 'types/ContactNote';
import { selectIsUsedInBr } from 'selectors/contactNoteListSelectors';
import { EnvConfig } from 'config/envConfig';
import { deserialiseData } from 'utils/noteDialogUtil';
import { User } from 'types/User';
import { ObjectSchema } from 'yup';

import { UserPartnerType } from './data/UserPartnerStatus';
import contactNoteDialogValidationSchema from './contactNoteDialogValidationSchema';
import { ContactNoteInitiatedUiScreen } from './data/ContactNoteDialogStatus';

type WithEditProp = {
  edit?: boolean;
  initialValues: ContactNoteDialog;
  validationSchema: ObjectSchema<any>;
};

const withInitialValuesFromContactNotesHome = <P extends object>(
  WrappedComponent: React.ComponentType<P & WithEditProp>,
): React.FC<Omit<P, keyof WithEditProp> & Pick<WithEditProp, 'edit'>> => {
  const ComponentWithInitialValues = (props: Omit<P, keyof WithEditProp> & Pick<WithEditProp, 'edit'>) => {
    const { edit = false, ...restProps } = props;
    const { techId } = useParams<{ techId: string }>();
    const shownNote: ContactNote | null = useSelector((state: GlobalState) => state.contactNoteList.shownNote);
    const usedInBr = useSelector(selectIsUsedInBr);

    const editFlowRelatedPartnerId = useMemo(() => {
      if (edit && shownNote) {
        return shownNote.relatedPartners.map((item) => item.partnerUId);
      }
      return [];
    }, [shownNote, edit]);

    const { data: multiplePartnerDetails } = useGetMultiplePartnerDetails(
      editFlowRelatedPartnerId,
      edit && editFlowRelatedPartnerId.length > 0,
    );

    const user: User = useSelector((state: GlobalState) => state.user.userDetails);
    const { data: partnerDetails } = useGetPartnerDetails(techId || '', usedInBr);

    const contactNoteToEdit: ContactNoteDialog = useMemo(() => {
      if (edit && shownNote) {
        const convertedNote = deserialiseData(shownNote);
        if ((multiplePartnerDetails?.length ?? 0) > 0) {
          const dataList = multiplePartnerDetails?.map((item) => {
            if (item.partnerDetails.partnerStructureType === 'INDIVIDUAL_PARTNER') {
              return { type: 'PI', partnerUid: item.partnerUId };
            }
            if (item.partnerDetails.partnerStructureType === 'LEGAL_ENTITY_PARTNER') {
              return { type: 'LE', partnerUid: item.partnerUId };
            }
          });
          convertedNote.relatedPartners = convertedNote.relatedPartners.map((item) => {
            const data = dataList?.find((object) => object?.partnerUid === item.value);
            return { value: item.value, type: data?.type };
          });
        }

        const relatedPartners =
          convertedNote?.otherPartner && !EnvConfig.config.enableBrClientRepresentativeTable
            ? [...convertedNote.relatedPartners, { value: 'other' }]
            : [...convertedNote.relatedPartners];
        return {
          ...convertedNote,
          partner: usedInBr
            ? null
            : {
                globalPartnerId: '',
                partnerUId: '',
                fullName: '',
                relationshipManager: {
                  uid: user.username,
                  fullName: `${user.givenName} ${user.sureName}`,
                },
              },
          //Cast boolean values to string so that it complies with Formik
          crossBorderActivity:
            shownNote.crossBorderActivity === null ? undefined : shownNote.crossBorderActivity.toString(),
          qualifiedClientContact:
            shownNote.qualifiedClientContact === null ? undefined : shownNote.qualifiedClientContact.toString(),
          notes: shownNote.notes || '',
          subject: shownNote.subject || '',
          br: {
            relatedBrUid: null,
          },
          relatedPartners,
          initiatedUiScreen: ContactNoteInitiatedUiScreen.HOME,
        };
      }
      return {
        partner: usedInBr
          ? null
          : {
              globalPartnerId: '',
              partnerUId: '',
              fullName: '',
              relationshipManager: {
                uid: user.username,
                fullName: `${user.givenName} ${user.sureName}`,
              },
            },

        additionalAttendees: [user.username],

        endDateTime: null,
        locationCountry: '',
        locationCity: '',

        createdBy: user.username,
        attachments: [],

        createdDateTime: null,
        globalPartnerId: '',
        source: '',
        tripId: '',
        updateDateTime: null,

        subject: '',
        notes: '',
        relatedPartners: [],
        otherPartner: '',
        otherBR: '',
        relatedBRs: [],
        topics: [],
        topicOther: '',
        initiatedBy: '',
        notifyOtherUser: [],
        qualifiedClientContact: undefined,
        crossBorderActivity: undefined,
        contactType: '',
        contactTypeOther: '',
        isTimeAllowed: false,
        br: {
          relatedBrUid: null,
        },
        startDate: null,
        startTime: null,
        endDate: null,
        endTime: null,
        direction: undefined,
        initiatedUiScreen: ContactNoteInitiatedUiScreen.HOME,
      } as any;
    }, [usedInBr, user, edit, shownNote, multiplePartnerDetails]);

    const isLE = useMemo(() => {
      if (partnerDetails) {
        return partnerDetails.partnerStructureType === 'LEGAL_ENTITY_PARTNER';
      }
      return false;
    }, [partnerDetails]);

    const isPartner = useMemo(() => {
      if (partnerDetails) {
        if (partnerDetails.partnerStructureType === 'INDIVIDUAL_PARTNER') {
          return partnerDetails.individualPartner.status === UserPartnerType.ONBOARDED;
        } else if (partnerDetails.partnerStructureType === 'LEGAL_ENTITY_PARTNER') {
          return partnerDetails.legalPartner.status === UserPartnerType.ONBOARDED;
        }
      }

      return false;
    }, [partnerDetails]);

    const validationSchema = useMemo(() => {
      return contactNoteDialogValidationSchema(usedInBr, isPartner, isLE, true);
    }, [usedInBr, isPartner, isLE]);

    return (
      <WrappedComponent
        {...(restProps as P)}
        initialValues={contactNoteToEdit}
        edit={edit}
        validationSchema={validationSchema}
        isPartnerBR={false}
      />
    );
  };

  return ComponentWithInitialValues;
};
export default withInitialValuesFromContactNotesHome;
